import React, { useContext, useEffect } from 'react';

import classnames from 'classnames';
import { ExternalLink } from 'lucide-react';

import Pill, { DefaultPillStyles } from 'common/common/Pill';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext } from 'common/containers/ModalContainer';
import { LocationContext, RouterContext } from 'common/containers/RouterContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import cloneElementWithProps from 'common/core/cloneElementWithProps';
import Helmet from 'common/helmets/Helmet';
import Link from 'common/Link';
import AccessModal from 'common/modals/AccessModal';
import { ExternalSuggestionsFilters, PostsFilters } from 'common/subdomain/admin/AdminQueueFilters';
import ButtonV2 from 'common/ui/ButtonV2';
import CountBadge from 'common/ui/CountBadge';
import { H1, P } from 'common/ui/Text';
import hasPermission from 'common/util/hasPermission';

import type { Board } from 'common/api/endpoints/boards';
import type { Company, RolePermissionName } from 'common/api/endpoints/companies';

import 'css/components/subdomain/admin/_AdminQueue.scss';

type Props = {
  children: React.ReactNode;
  boards: Board[];
  inboxTotalItemCount: number; // spam count
  queueItemListTotalCount: number; // external-suggestions count
  queuePostItemListTotalCount: number; // feature-requests count
};

const AdminQueue = (props: Props) => {
  const router = useContext(RouterContext);
  const location = useContext(LocationContext);
  const company = useContext<Company>(CompanyContext);
  const viewer = useContext(ViewerContext);
  const openModal = useContext(OpenModalContext);
  const isAllowed = hasPermission('deleteComments', company, viewer);

  useEffect(() => {
    if (!isAllowed) {
      const permission: RolePermissionName = 'deletePosts';
      router.replace('/admin');
      openModal(
        AccessModal,
        {
          requiredPermissions: [permission],
        },
        {
          allowRouteChange: true,
        }
      );
    }
  }, [router, isAllowed, openModal]);

  return (
    <div className="adminQueue">
      <Helmet title="Inbox | Canny" />
      <header className="queueHeader">
        <div className="queueTitle">
          <H1 variant="headingLg">Inbox</H1>
          <Pill className="queueBetaPill" pillStyle={DefaultPillStyles.canny}>
            Beta
          </Pill>
        </div>
        <P className="queueSubtitle">
          Review incoming feedback from all your sources.{' '}
          <a
            href="https://help.canny.io/en/articles/8202451-beta-inbox"
            target="_blank"
            className="externalLink"
            rel="noopener">
            Learn&nbsp;more.
          </a>
        </P>
        <div className="buttons">
          <div className="pageToggle">
            <Link
              to="/admin/inbox/external-suggestions"
              className={classnames({
                option: true,
                selected: location.pathname.includes('/admin/inbox/external-suggestions'),
              })}>
              External Suggestions{' '}
              <CountBadge size="small" max={500} count={props.queueItemListTotalCount} />
            </Link>
            <Link
              to="/admin/inbox/canny-posts"
              className={classnames({
                option: true,
                selected: location.pathname.includes('/admin/inbox/canny-posts'),
              })}>
              Canny Posts{' '}
              <CountBadge size="small" max={500} count={props.queuePostItemListTotalCount} />
            </Link>
            {company.features.moderationTools && (
              <Link
                to="/admin/inbox/spam"
                className={classnames({
                  option: true,
                  selected: location.pathname.includes('/admin/inbox/spam'),
                })}>
                Spam <CountBadge size="small" max={500} count={props.inboxTotalItemCount} />
              </Link>
            )}
          </div>
          <div className="right">
            {location.pathname.includes('/admin/inbox/external-suggestions') && (
              <ExternalSuggestionsFilters boards={props.boards} />
            )}
            {location.pathname.includes('/admin/inbox/canny-posts') && (
              <PostsFilters boards={props.boards} />
            )}
            <a
              href="https://feedback.canny.io/inbox-beta"
              target="_blank"
              rel="noopener noreferrer">
              <ButtonV2 startIcon={ExternalLink} variant="outlined" size="medium">
                Give inbox feedback
              </ButtonV2>
            </a>
          </div>
        </div>
      </header>
      <section className="adminQueueContent">
        {isAllowed && cloneElementWithProps(props.children, props)}
      </section>
    </div>
  );
};

export default AdminQueue;
